import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeFor } from "./context/AuthContext";
import Login from "./pages/Login";
import InvestorDashboard from "./pages/investor/InvestorDashboard";
import PortfolioPage from "./pages/investor/PortfolioPage";
import HoldingsPage from "./pages/investor/HoldingsPage";
import TransactionsPage from "./pages/investor/TransactionsPage";
import PerformancePage from "./pages/investor/PerformancePage";
import DocumentsPage from "./pages/investor/DocumentsPage";
import InvestorRequests from "./pages/investor/InvestorRequests";
import InvestmentRiskPage from "./pages/investor/InvestmentRiskPage";
import ProfilePage from "./pages/investor/ProfilePage";
import SupportPage from "./pages/investor/SupportPage";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientNew from "./pages/admin/AdminClientNew";
import AdminClientDetail from "./pages/admin/AdminClientDetail";
import AdminWrms from "./pages/admin/AdminWrms";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminKyc from "./pages/admin/AdminKyc";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminRequests from "./pages/admin/AdminRequests";
import AdminSettings from "./pages/admin/AdminSettings";
import Calculators from "./pages/Calculators";
import WrmOverview from "./pages/wrm/WrmOverview";
import WrmClients from "./pages/wrm/WrmClients";
import WrmClientDetail from "./pages/wrm/WrmClientDetail";
import WrmRequests from "./pages/wrm/WrmRequests";
import WrmMyPay from "./pages/wrm/WrmMyPay";

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-[#030706] flex items-center justify-center" data-testid="app-loader">
      <div className="h-9 w-9 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
    </div>
  );
}

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  return <Navigate to={user ? homeFor(user.role) : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors theme="dark" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<HomeRedirect />} />

            <Route path="/dashboard" element={<Protected roles={["investor"]}><InvestorDashboard /></Protected>} />
            <Route path="/portfolio" element={<Protected roles={["investor"]}><PortfolioPage /></Protected>} />
            <Route path="/performance" element={<Protected roles={["investor"]}><PerformancePage /></Protected>} />
            <Route path="/holdings" element={<Protected roles={["investor"]}><HoldingsPage /></Protected>} />
            <Route path="/transactions" element={<Protected roles={["investor"]}><TransactionsPage /></Protected>} />
            <Route path="/documents" element={<Protected roles={["investor"]}><DocumentsPage /></Protected>} />
            <Route path="/requests" element={<Protected roles={["investor"]}><InvestorRequests /></Protected>} />
            <Route path="/investment-risk" element={<Protected roles={["investor"]}><InvestmentRiskPage /></Protected>} />
            <Route path="/investment-plan" element={<Navigate to="/investment-risk" replace />} />
            <Route path="/risk-profile" element={<Navigate to="/investment-risk" replace />} />
            <Route path="/profile" element={<Protected roles={["investor"]}><ProfilePage /></Protected>} />
            <Route path="/support" element={<Protected roles={["investor"]}><SupportPage /></Protected>} />
            <Route path="/calculators" element={<Protected roles={["investor", "admin", "wrm"]}><Calculators /></Protected>} />

            <Route path="/admin" element={<Protected roles={["admin"]}><AdminOverview /></Protected>} />
            <Route path="/admin/clients" element={<Protected roles={["admin"]}><AdminClients /></Protected>} />
            <Route path="/admin/clients/new" element={<Protected roles={["admin"]}><AdminClientNew /></Protected>} />
            <Route path="/admin/clients/:id" element={<Protected roles={["admin"]}><AdminClientDetail /></Protected>} />
            <Route path="/admin/wrms" element={<Protected roles={["admin"]}><AdminWrms /></Protected>} />
            <Route path="/admin/transactions" element={<Protected roles={["admin"]}><AdminTransactions /></Protected>} />
            <Route path="/admin/kyc" element={<Protected roles={["admin"]}><AdminKyc /></Protected>} />
            <Route path="/admin/audit" element={<Protected roles={["admin"]}><AdminAudit /></Protected>} />
            <Route path="/admin/requests" element={<Protected roles={["admin"]}><AdminRequests /></Protected>} />
            <Route path="/admin/settings" element={<Protected roles={["admin"]}><AdminSettings /></Protected>} />

            <Route path="/wrm" element={<Protected roles={["wrm"]}><WrmOverview /></Protected>} />
            <Route path="/wrm/clients" element={<Protected roles={["wrm"]}><WrmClients /></Protected>} />
            <Route path="/wrm/clients/:id" element={<Protected roles={["wrm"]}><WrmClientDetail /></Protected>} />
            <Route path="/wrm/requests" element={<Protected roles={["wrm"]}><WrmRequests /></Protected>} />
            <Route path="/wrm/pay" element={<Protected roles={["wrm"]}><WrmMyPay /></Protected>} />

            <Route path="*" element={<HomeRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
